/**
 * Contenido de la plantilla. Edita aquí textos, secciones, carta, servicios o campos de formulario: los componentes de
 * src/components/industry se adaptan solos. Las imágenes viven en src/content/website.ts (siteImages): cada sección
 * indica la posición de la imagen que usa (0 = primera). El nombre, el lema, el correo y el teléfono están en siteConfig.ts.
 */
export interface Link { label: string; href: string }

export type HeroVariant = "split" | "editorial" | "fullscreen" | "framed" | "panorama" | "minimal" | "search" | "console" | "collage" | "card" | "statement" | "centered";
export interface HeroSection {
  type: "hero"; id: string; variant: HeroVariant;
  eyebrow: string; title: string; text: string; primary: Link; secondary?: Link;
  images: number[]; stats?: { value: string; label: string }[]; note?: string;
  /** Pies de foto de las imágenes secundarias (portadas «panorama» y «collage»). */
  captions?: string[];
  /** Buscador demostrativo (portada «search»): abre el formulario de contacto con los datos elegidos. */
  search?: { fields: FormField[]; submitLabel: string; target: string };
  /** Tarjeta destacada (portada «card»): horario, cita o datos clave. */
  card?: { title: string; items: string[]; cta: Link };
  /** Muestra una maqueta de la aplicación dibujada con CSS en lugar de una foto (portada «centered»). */
  mock?: { title: string; metrics: { label: string; value: string }[]; rows: string[] };
  /** Sellos que flotan sobre la fotografía (portadas «split» y «console»): opiniones, certificaciones, disponibilidad. */
  chips?: { icon?: IconName; label: string }[];
  /** Ventana de código en lugar de la fotografía (portada «console»), útil para portafolios de desarrollo. */
  code?: { file: string; lines: string[] };
}
export type IconName = "shield" | "clock" | "spark" | "leaf" | "chart" | "users" | "tool" | "globe" | "heart" | "check" | "map" | "cup" | "code" | "lock" | "bolt" | "home" | "star" | "plane";
export interface IndustriesSection { type: "industries"; id: string; eyebrow: string; title: string; items: { name: string; text: string }[] }
export interface CapabilitiesSection {
  type: "capabilities"; id: string; eyebrow: string; title: string; text: string;
  items: { code: string; title: string; text: string; specs: string[] }[];
}
export interface ProcessSection { type: "process"; id: string; eyebrow: string; title: string; image?: number; steps: { title: string; text: string }[] }
export interface GallerySection {
  type: "gallery"; id: string; variant: "facilities" | "portfolio" | "strip" | "mosaic";
  eyebrow: string; title: string; text?: string; items: { image: number; caption: string }[];
}
/**
 * Carrusel de imágenes: una foto grande a la vez, con flechas, puntos, gestos táctiles y teclado.
 * «autoplay» lo avanza solo cada «interval» milisegundos (se pausa al pasar el ratón, al enfocarlo y si el visitante
 * pidió reducir el movimiento). «text» es opcional y se muestra bajo el pie de foto.
 */
export interface CarouselSection {
  type: "carousel"; id: string; eyebrow: string; title: string; text?: string;
  autoplay?: boolean; interval?: number; items: { image: number; caption: string; text?: string }[];
}
export interface CertificationsSection { type: "certifications"; id: string; eyebrow: string; title: string; text: string; items: { name: string; text: string; icon?: IconName }[]; note: string }
export interface ServicesSection {
  type: "services"; id: string; variant: "list" | "cards"; eyebrow: string; title: string; text?: string;
  items: { name: string; text: string; duration?: string; price?: string }[]; note?: string;
}
export interface PackagesSection {
  type: "packages"; id: string; eyebrow: string; title: string; note?: string;
  items: { name: string; price: string; text: string; features: string[]; featured?: boolean }[];
}
export interface TeamSection { type: "team"; id: string; eyebrow: string; title: string; items: { name: string; role: string; text: string }[] }
/** «rating» (1 a 5) dibuja estrellas con iconos; publícalo solo si la calificación es real. */
export interface TestimonialsSection { type: "testimonials"; id: string; eyebrow: string; title: string; items: { quote: string; author: string; detail: string; rating?: number }[]; note: string }
export interface StorySection { type: "story"; id: string; eyebrow: string; title: string; paragraphs: string[]; image: number; signature?: string }
export interface MenuSection {
  type: "menu"; id: string; eyebrow: string; title: string; text?: string; note: string;
  categories: { name: string; items: { name: string; text: string; price: string; tags?: string[] }[] }[];
}
export interface SpecialtiesSection { type: "specialties"; id: string; eyebrow: string; title: string; items: { name: string; text: string; price: string; image: number }[] }
export interface HoursSection { type: "hours"; id: string; eyebrow: string; title: string; text: string }
export interface FormField {
  name: string; label: string; type: "text" | "email" | "tel" | "date" | "time" | "number" | "select" | "textarea";
  required?: boolean; options?: string[]; autocomplete?: string; half?: boolean;
}
export interface FormSection {
  type: "form"; id: string; variant: "quote" | "booking" | "reservation"; eyebrow: string; title: string; text: string;
  submitLabel: string; fields: FormField[]; aside?: string[]; note: string;
}

export interface ListingItem {
  title: string; image: number; text: string; category?: string; location?: string; price?: string; priceNote?: string;
  meta: string[]; badge?: string; details?: { description: string; features: string[] };
  /** Enlace externo (demo, repositorio); con «href» vacío no se muestra. */
  link?: Link;
}
/** Fichas (habitaciones, destinos, propiedades, proyectos o productos) con filtro opcional y ficha detallada en un diálogo. */
export interface ListingSection {
  type: "listing"; id: string; variant: "rooms" | "tours" | "properties" | "projects" | "products";
  eyebrow: string; title: string; text?: string; filterLabel?: string; categories?: string[]; items: ListingItem[]; note?: string; cta?: Link;
}
export interface FeaturesSection { type: "features"; id: string; variant: "icons" | "list" | "cards"; eyebrow: string; title: string; text?: string; items: { icon?: IconName; title: string; text: string }[] }
export interface StatsSection { type: "stats"; id: string; eyebrow?: string; title?: string; items: { value: string; label: string }[]; note?: string }
export interface FaqSection { type: "faq"; id: string; eyebrow: string; title: string; items: { question: string; answer: string }[] }
export interface TimelineSection { type: "timeline"; id: string; eyebrow: string; title: string; items: { year: string; title: string; text: string }[] }
export interface LogosSection { type: "logos"; id: string; title: string; items: string[]; note?: string }
export interface CtaSection { type: "cta"; id: string; eyebrow?: string; title: string; text: string; primary: Link; secondary?: Link; image?: number }

export type Section = HeroSection | ListingSection | FeaturesSection | StatsSection | FaqSection | TimelineSection | LogosSection | CtaSection | IndustriesSection | CapabilitiesSection | ProcessSection | GallerySection | CarouselSection | CertificationsSection
  | ServicesSection | PackagesSection | TeamSection | TestimonialsSection | StorySection | MenuSection | SpecialtiesSection | HoursSection | FormSection;

export interface IndustryContent {
  id: string;
  /** Estructura (arquetipo) y dirección de arte: cambian la composición en styles/industry.css. */
  archetype: string;
  style: string;
  header: { variant: "compact" | "centered" | "transparent" | "minimal" | "stacked" | "floating"; topbar: string[]; cta: Link };
  nav: Link[];
  /** Quita, reordena o duplica secciones: la página las dibuja en este orden. */
  sections: Section[];
  contact: { address: string; hours: { days: string; time: string }[]; mapUrl: string; social: Link[] };
  footer: { about: string; legal: string };
  disclaimer: string;
  /** Botón flotante de WhatsApp (usa el teléfono de siteConfig.ts). Pon «enabled: false» para ocultarlo. */
  whatsapp?: { enabled?: boolean; message?: string; label?: string };
}

export const industry: IndustryContent = {
  "id": "agencia-viajes-rumbo-norte",
  "archetype": "storytelling-scroll",
  "style": "travel-immersive",
  "header": {
    "variant": "transparent",
    "topbar": [],
    "cta": {
      "label": "Planear mi viaje",
      "href": "#quote"
    }
  },
  "nav": [
    {
      "label": "Destinos",
      "href": "#destinations"
    },
    {
      "label": "Cómo trabajamos",
      "href": "#process"
    },
    {
      "label": "Qué incluye",
      "href": "#included"
    },
    {
      "label": "Opiniones",
      "href": "#testimonials"
    },
    {
      "label": "Preguntas",
      "href": "#faq"
    }
  ],
  "sections": [
    {
      "type": "hero",
      "id": "hero",
      "variant": "panorama",
      "eyebrow": "Agencia de viajes · Monterrey",
      "title": "El próximo recuerdo empieza aquí",
      "text": "Diseñamos itinerarios a tu ritmo: lagos de montaña, playas escondidas y ciudades con historia. Tú eliges el destino; nosotros cuidamos cada detalle.",
      "primary": {
        "label": "Planear mi viaje",
        "href": "#quote"
      },
      "secondary": {
        "label": "Ver destinos",
        "href": "#destinations"
      },
      "images": [
        0,
        1,
        2,
        3
      ],
      "captions": [
        "Caribe mexicano",
        "Cascos antiguos de Europa",
        "Desiertos y cañones"
      ],
      "note": "Itinerarios desde 4 días · grupos pequeños o viajes privados"
    },
    {
      "type": "listing",
      "id": "destinations",
      "variant": "tours",
      "eyebrow": "Destinos",
      "title": "Viajes que ya hemos probado",
      "text": "Cada propuesta se ajusta a tus fechas, presupuesto y forma de viajar.",
      "filterLabel": "Filtrar por tipo de viaje",
      "categories": [
        "Naturaleza",
        "Ciudades",
        "Aventura"
      ],
      "items": [
        {
          "title": "Glaciares y lagos de montaña",
          "image": 4,
          "category": "Naturaleza",
          "location": "Montañas Rocosas",
          "text": "Senderos fáciles, lagos turquesa y cabañas con chimenea.",
          "price": "Desde $38 900",
          "priceNote": "por persona",
          "meta": [
            "8 días",
            "Grupo pequeño",
            "Junio – septiembre"
          ],
          "details": {
            "description": "Un recorrido por parques nacionales con caminatas guiadas de dificultad baja y media, hospedaje en cabañas y tiempo libre para fotografía.",
            "features": [
              "Vuelos redondos desde Monterrey",
              "7 noches en cabañas y hoteles boutique",
              "Guía naturalista en español",
              "Entradas a parques nacionales"
            ]
          }
        },
        {
          "title": "Lagos de otoño",
          "image": 5,
          "category": "Naturaleza",
          "location": "Montañas de Asia",
          "text": "Bosques de colores y aguas cristalinas en la mejor temporada.",
          "price": "Desde $46 500",
          "priceNote": "por persona",
          "badge": "Temporada",
          "meta": [
            "10 días",
            "Viaje privado",
            "Octubre"
          ],
          "details": {
            "description": "Diez días entre lagos, aldeas y templos en la temporada de follaje, con traslados privados y hoteles con encanto.",
            "features": [
              "Traslados privados",
              "Hoteles boutique",
              "Guía local",
              "Seguro de viaje"
            ]
          }
        },
        {
          "title": "Capitales con historia",
          "image": 6,
          "category": "Ciudades",
          "location": "Europa central",
          "text": "Cascos antiguos, cafés y museos con tiempo para perderse.",
          "price": "Desde $42 300",
          "priceNote": "por persona",
          "meta": [
            "9 días",
            "Grupo pequeño",
            "Todo el año"
          ],
          "details": {
            "description": "Tres ciudades conectadas en tren con recorridos a pie por sus centros históricos y noches libres.",
            "features": [
              "Trenes entre ciudades",
              "Hoteles céntricos",
              "Recorridos guiados a pie",
              "Tarjeta de transporte local"
            ]
          }
        },
        {
          "title": "Cañones y miradores",
          "image": 7,
          "category": "Aventura",
          "location": "Suroeste de Estados Unidos",
          "text": "Caminatas al amanecer, cielos estrellados y carreteras escénicas.",
          "price": "Desde $29 800",
          "priceNote": "por persona",
          "meta": [
            "7 días",
            "Viaje en auto",
            "Marzo – mayo"
          ],
          "details": {
            "description": "Ruta en auto por parques nacionales con caminatas de medio día y noches en hoteles junto a los miradores.",
            "features": [
              "Renta de auto con seguro",
              "Itinerario de manejo detallado",
              "Hoteles junto a los parques",
              "Asistencia 24/7"
            ]
          }
        }
      ],
      "note": "Precios de referencia en MXN sujetos a disponibilidad y tipo de cambio.",
      "cta": {
        "label": "Cotizar este viaje",
        "href": "#quote"
      }
    },
    {
      "type": "process",
      "id": "process",
      "eyebrow": "Cómo trabajamos",
      "title": "De la idea a la maleta en tres pasos",
      "steps": [
        {
          "title": "Conversamos",
          "text": "Nos cuentas qué te emociona, cuándo puedes viajar y con quién. Sin compromiso."
        },
        {
          "title": "Diseñamos tu itinerario",
          "text": "Recibes una propuesta día por día con hospedaje, traslados y precio final."
        },
        {
          "title": "Viajas acompañado",
          "text": "Antes y durante el viaje tienes un asesor disponible por mensaje las 24 horas."
        }
      ]
    },
    {
      "type": "features",
      "id": "included",
      "variant": "icons",
      "eyebrow": "Qué incluye",
      "title": "Viajar tranquilo también se planea",
      "items": [
        {
          "icon": "plane",
          "title": "Vuelos y traslados",
          "text": "Coordinamos conexiones cómodas y traslados desde que aterrizas."
        },
        {
          "icon": "shield",
          "title": "Seguro de viaje",
          "text": "Cobertura médica y de equipaje en todos nuestros itinerarios."
        },
        {
          "icon": "clock",
          "title": "Asistencia 24/7",
          "text": "Un asesor real responde por mensaje en tu idioma."
        },
        {
          "icon": "map",
          "title": "Guías locales",
          "text": "Personas del lugar que comparten su forma de vivirlo."
        },
        {
          "icon": "home",
          "title": "Hospedaje con encanto",
          "text": "Hoteles pequeños y bien ubicados, elegidos por nosotros."
        },
        {
          "icon": "leaf",
          "title": "Turismo responsable",
          "text": "Proveedores locales y grupos pequeños para cuidar cada destino."
        }
      ]
    },
    {
      "type": "testimonials",
      "id": "testimonials",
      "eyebrow": "Opiniones",
      "title": "Historias de quienes ya viajaron",
      "items": [
        {
          "quote": "No tuvimos que preocuparnos por nada. Cada traslado y cada hotel estaban exactamente como lo prometieron.",
          "author": "Viajera de ejemplo",
          "detail": "Glaciares y lagos"
        },
        {
          "quote": "Nos ayudaron a cambiar un vuelo a medianoche desde el otro lado del mundo. Eso no tiene precio.",
          "author": "Viajero de ejemplo",
          "detail": "Lagos de otoño"
        },
        {
          "quote": "El itinerario tenía el equilibrio perfecto entre recorridos y tiempo libre.",
          "author": "Viajera de ejemplo",
          "detail": "Capitales con historia"
        }
      ],
      "note": "Testimonios de ejemplo: sustitúyelos por reseñas reales publicadas con autorización."
    },
    {
      "type": "faq",
      "id": "faq",
      "eyebrow": "Preguntas frecuentes",
      "title": "Antes de reservar",
      "items": [
        {
          "question": "¿Con cuánta anticipación debo planear?",
          "answer": "Recomendamos de tres a seis meses para viajes internacionales, sobre todo en temporada alta."
        },
        {
          "question": "¿Puedo pagar en mensualidades?",
          "answer": "Sí, ofrecemos planes de pago con anticipo y liquidación antes de la salida."
        },
        {
          "question": "¿Qué pasa si debo cancelar?",
          "answer": "Cada proveedor tiene sus condiciones; te las explicamos por escrito antes de confirmar y te recomendamos un seguro de cancelación."
        },
        {
          "question": "¿Necesito visa?",
          "answer": "Te indicamos los requisitos de cada destino y te orientamos con el trámite cuando aplica."
        }
      ]
    },
    {
      "type": "form",
      "id": "quote",
      "variant": "quote",
      "eyebrow": "Planea tu viaje",
      "title": "Cuéntanos a dónde quieres ir",
      "text": "Te enviamos una propuesta personalizada en un máximo de dos días hábiles.",
      "submitLabel": "Solicitar propuesta",
      "aside": [
        "Propuesta sin costo",
        "Asesor dedicado durante todo el viaje",
        "Pagos seguros con factura"
      ],
      "fields": [
        {
          "name": "nombre",
          "label": "Nombre",
          "type": "text",
          "required": true,
          "autocomplete": "name",
          "half": true
        },
        {
          "name": "correo",
          "label": "Correo",
          "type": "email",
          "required": true,
          "autocomplete": "email",
          "half": true
        },
        {
          "name": "destino",
          "label": "Destino o idea",
          "type": "text",
          "required": true,
          "half": true
        },
        {
          "name": "fechas",
          "label": "Mes aproximado",
          "type": "text",
          "half": true
        },
        {
          "name": "viajeros",
          "label": "Viajeros",
          "type": "select",
          "options": [
            "1",
            "2",
            "3 a 4",
            "5 o más"
          ],
          "half": true
        },
        {
          "name": "presupuesto",
          "label": "Presupuesto por persona",
          "type": "select",
          "options": [
            "Hasta $25 000",
            "$25 000 a $45 000",
            "Más de $45 000"
          ],
          "half": true
        },
        {
          "name": "detalles",
          "label": "¿Qué te gustaría vivir?",
          "type": "textarea"
        }
      ],
      "note": "El formulario abre el correo del visitante con los datos listos para enviar; no guarda información."
    }
  ],
  "contact": {
    "address": "Av. Vasconcelos 300, San Pedro Garza García, N.L.",
    "hours": [
      {
        "days": "Lunes a viernes",
        "time": "9:00 – 19:00"
      },
      {
        "days": "Sábado",
        "time": "10:00 – 14:00"
      }
    ],
    "mapUrl": "https://www.openstreetmap.org/search?query=San%20Pedro%20Garza%20Garc%C3%ADa",
    "social": [
      {
        "label": "Instagram",
        "href": "https://www.instagram.com/"
      },
      {
        "label": "YouTube",
        "href": "https://www.youtube.com/"
      }
    ]
  },
  "footer": {
    "about": "Agencia de viajes a la medida con asesoría antes, durante y después de tu viaje.",
    "legal": "Contenido de ejemplo generado con WebForge Studio."
  },
  "disclaimer": "Agencia ficticia. Itinerarios, precios y opiniones son de ejemplo."
};
